import React from 'react';
import { Box, Button, Text, VStack, Heading, AlertRoot, AlertContent } from '@chakra-ui/react';
import { apiCircuitBreaker, assetCircuitBreaker, presignedUrlCircuitBreaker } from '../utils/circuitBreaker';

let posthog = null;
try {
  posthog = require('posthog-js').default || require('posthog-js');
} catch (_) {}

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      errorType: null,
      isRecoverable: true
    };
  }

  static getDerivedStateFromError(error) {
    // Detect error type
    const errorType = error?.code || error?.name || 'UNKNOWN_ERROR';
    const isRecoverable = error?.recoverable !== false;

    return {
      hasError: true,
      errorId: Date.now().toString(36) + Math.random().toString(36).substr(2),
      errorType,
      isRecoverable
    };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Detect if this is an API-related error
    const isApiError = this.isApiError(error);
    const isNetworkError = this.isNetworkError(error);
    const isCircuitBreakerError = error.message?.includes('circuit breaker');

    // You can also log the error to an error reporting service
    this.logErrorToService(error, errorInfo, { isApiError, isNetworkError, isCircuitBreakerError });

    this.setState({
      error,
      errorInfo
    });
  }

  isApiError = (error) => {
    if (!error) return false;
    return error.message?.includes('API') ||
           error.message?.includes('fetch') ||
           error.message?.includes('network') ||
           error.message?.includes('timeout') ||
           error.code === 'ERR_NETWORK' ||
           error.code === 'ECONNREFUSED';
  };

  isNetworkError = (error) => {
    if (!error) return false;
    return error.code === 'ERR_NETWORK' ||
           error.code === 'ECONNREFUSED' ||
           error.message?.includes('network') ||
           error.message?.includes('Failed to fetch');
  };

  logErrorToService = (error, errorInfo, metadata = {}) => {
    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      errorType: error.code || error.name,
      metadata
    };

    console.error('Error logged:', errorData);

    if (posthog && posthog.captureException) {
      try {
        posthog.captureException(error, {
          context: {
            ...metadata,
            componentStack: errorInfo.componentStack,
            url: window.location.href
          }
        });
      } catch (_) {}
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  handleRetry = () => {
    // Reset circuit breakers on manual retry
    try {
      apiCircuitBreaker.reset();
      assetCircuitBreaker.reset();
      presignedUrlCircuitBreaker.reset();
    } catch (err) {
      console.warn('Could not reset circuit breakers:', err);
    }

    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
      errorType: null,
      isRecoverable: true
    });
  };

  getUserFriendlyMessage = () => {
    const { error } = this.state;

    if (this.isNetworkError(error)) {
      return {
        title: 'Connection Problem',
        message: 'Unable to connect to the server. Please check your internet connection and try again.',
        icon: '🌐'
      };
    }

    if (error?.message?.includes('circuit breaker')) {
      return {
        title: 'Service Temporarily Unavailable',
        message: 'We\'re experiencing technical difficulties. Please wait a moment before trying again.',
        icon: '⚡'
      };
    }

    if (this.isApiError(error)) {
      return {
        title: 'Something Went Wrong',
        message: 'We encountered an error while loading data. This has been logged and we\'ll look into it.',
        icon: '⚠️'
      };
    }

    return {
      title: 'Unexpected Error',
      message: 'Something went wrong. We\'ve logged this error and will investigate.',
      icon: '❌'
    };
  };

  render() {
    if (this.state.hasError) {
      const { error, errorId, isRecoverable } = this.state;
      const friendlyError = this.getUserFriendlyMessage();

      // Custom error UI
      return (
        <Box
          minH="100vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
          bg="gray.50"
          p={4}
        >
          <Box maxW="md" w="full">
            <AlertRoot status="error" borderRadius="lg" p={6}>
              <AlertContent>
                <VStack spacing={4} align="stretch">
                <VStack spacing={2} align="center">
                  <Text fontSize="4xl">{friendlyError.icon}</Text>
                  <Heading size="lg" color="red.600">
                    {friendlyError.title}
                  </Heading>
                  <Text textAlign="center" color="red.700">
                    {friendlyError.message}
                  </Text>
                  {!isRecoverable && (
                    <Text fontSize="sm" color="red.600" fontWeight="bold">
                      Please reload the page to continue.
                    </Text>
                  )}
                </VStack>

                {process.env.NODE_ENV === 'development' && error && (
                  <Box
                    bg="red.50"
                    p={3}
                    borderRadius="md"
                    border="1px"
                    borderColor="red.200"
                  >
                    <Text fontSize="sm" fontWeight="bold" color="red.800" mb={2}>
                      Error Details (Development Only):
                    </Text>
                    <Text fontSize="xs" color="red.700" fontFamily="mono" mb={2}>
                      {error.message}
                    </Text>
                    {error.stack && (
                      <Text fontSize="xs" color="red.600" fontFamily="mono" maxH="200px" overflowY="auto">
                        {error.stack}
                      </Text>
                    )}
                  </Box>
                )}

                <VStack spacing={2}>
                  {isRecoverable && (
                    <Button
                      colorScheme="red"
                      onClick={this.handleRetry}
                      size="sm"
                      w="full"
                    >
                      Try Again
                    </Button>
                  )}
                  <Button
                    variant={isRecoverable ? "outline" : "solid"}
                    colorScheme={isRecoverable ? "gray" : "red"}
                    onClick={this.handleReload}
                    size="sm"
                    w="full"
                  >
                    Reload Page
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    w="full"
                    onClick={() => window.history.back()}
                  >
                    Go Back
                  </Button>
                </VStack>

                {errorId && (
                  <Text fontSize="xs" color="gray.500" textAlign="center">
                    Error ID: {errorId}
                  </Text>
                )}
                </VStack>
              </AlertContent>
            </AlertRoot>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Functional wrapper component for easier use
export const withErrorBoundary = (Component, errorBoundaryProps = {}) => {
  const WrappedComponent = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
};

// Hook for handling async errors in functional components
export const useErrorHandler = () => {
  return (error, errorInfo = {}) => {
    // In a functional component, we can't use componentDidCatch
    // so we'll throw the error to be caught by the nearest error boundary
    console.error('Async error caught:', error, errorInfo);

    // Create an error object that will be caught by the error boundary
    const errorToThrow = new Error(error.message || 'An async error occurred');
    errorToThrow.originalError = error;
    errorToThrow.errorInfo = errorInfo;

    throw errorToThrow;
  };
};

export default ErrorBoundary;
